import React, { useState, useEffect, useMemo } from 'react';
import { Grid, Typography, Button, Pagination, FormControl, InputLabel, Select, MenuItem, Chip, Box, IconButton, Menu, TextField, InputAdornment, CircularProgress } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import SearchIcon from '@mui/icons-material/Search';
import { EditarDocumento } from './EditarDocumento';
import { useNavigate, useLocation } from 'react-router-dom';
import { SubHeader } from '../../utils/componentes-utiles/SubHeader';
import { CrearDocumento } from './CrearDocumento';

// Mock data para documentos - reemplazar con API real cuando esté disponible
const mockDocumentos = [
    {
        id: 1,
        nombre: 'Contrato de Trabajo Estándar',
        empresas: ['San Alfonso', 'Empresa ABC'],
        tipo: 'Obligatorio',
        tipo_firma: 'Firma electrónica',
        privacidad: 'Público',
        entidades: ['Personas'],
        por_defecto: true,
        fecha_creacion: '2024-01-15',
        estado: 'activo'
    },
    {
        id: 2,
        nombre: 'Acuerdo de Confidencialidad',
        empresas: ['San Alfonso'],
        tipo: 'Opcional',
        tipo_firma: 'Firma manuscrita',
        privacidad: 'Privado',
        entidades: ['Personas', 'Empresas'],
        por_defecto: false,
        fecha_creacion: '2024-02-20',
        estado: 'activo'
    },
    {
        id: 3,
        nombre: 'Manual de Procedimientos',
        empresas: ['Empresa ABC', 'Empresa XYZ'],
        tipo: 'Obligatorio',
        tipo_firma: 'Firma digital',
        privacidad: 'Público',
        entidades: ['Departamentos'],
        por_defecto: true,
        fecha_creacion: '2024-03-10',
        estado: 'inactivo'
    }
];

export const Documentos = () => {
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuRowId, setMenuRowId] = useState(null);
    const [editRowId, setEditRowId] = useState(null);
    const [openCreate, setOpenCreate] = useState(false);
    const [documentos, setDocumentos] = useState([]);
    const [documentosFiltrados, setDocumentosFiltrados] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');

    // Estados de filtros
    const [busqueda, setBusqueda] = useState('');
    const [filtroTipo, setFiltroTipo] = useState('');
    const [filtroPrivacidad, setFiltroPrivacidad] = useState('');
    const [filtroEstado, setFiltroEstado] = useState('');
    const [filtroEmpresa, setFiltroEmpresa] = useState('');

    // Paginación
    const [page, setPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);

    const navigate = useNavigate();
    const location = useLocation();

    const isActive = (path) => location.pathname === path;

    // Cargar documentos (mock por ahora)
    const cargarDocumentos = async () => {
        setLoaded(false);
        setError('');
        try {
            // Simular llamada a API
            await new Promise(resolve => setTimeout(resolve, 500));
            setDocumentos(mockDocumentos);
            setDocumentosFiltrados(mockDocumentos);
        } catch (err) {
            setError('Error al cargar documentos');
            console.error('Error cargando documentos:', err);
        } finally {
            setLoaded(true);
        }
    };

    // Aplicar filtros
    const aplicarFiltros = () => {
        let filtrados = [...documentos];

        // Filtro de búsqueda
        if (busqueda.trim()) {
            const termino = busqueda.toLowerCase().trim();
            filtrados = filtrados.filter(doc =>
                doc.nombre.toLowerCase().includes(termino) ||
                doc.empresas.some(emp => emp.toLowerCase().includes(termino)) ||
                doc.entidades.some(ent => ent.toLowerCase().includes(termino))
            );
        }

        // Filtro por tipo
        if (filtroTipo) {
            filtrados = filtrados.filter(doc => doc.tipo === filtroTipo);
        }

        // Filtro por privacidad
        if (filtroPrivacidad) {
            filtrados = filtrados.filter(doc => doc.privacidad === filtroPrivacidad);
        }

        // Filtro por estado
        if (filtroEstado) {
            filtrados = filtrados.filter(doc => doc.estado === filtroEstado);
        }

        // Filtro por empresa
        if (filtroEmpresa) {
            filtrados = filtrados.filter(doc =>
                doc.empresas.includes(filtroEmpresa)
            );
        }

        setDocumentosFiltrados(filtrados);
        setPage(1); // Resetear a primera página
    };

    // Aplicar filtros cuando cambien
    useEffect(() => {
        if (loaded) {
            aplicarFiltros();
        }
    }, [busqueda, filtroTipo, filtroPrivacidad, filtroEstado, filtroEmpresa, documentos, loaded]);

    // Cargar datos al montar componente
    useEffect(() => {
        cargarDocumentos();
    }, []);

    // Paginación
    const totalPages = Math.ceil(documentosFiltrados.length / itemsPerPage);
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const documentosPaginados = documentosFiltrados.slice(startIndex, endIndex);

    const handleOpenMenu = (event, rowId) => {
        setMenuAnchor(event.currentTarget);
        setMenuRowId(rowId);
    };

    const handleCloseMenu = () => {
        setMenuAnchor(null);
        setMenuRowId(null);
    };

    const handleOpenEdit = () => {
        setEditRowId(menuRowId);
        handleCloseMenu();
    };

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    // Limpiar filtros
    const limpiarFiltros = () => {
        setBusqueda('');
        setFiltroTipo('');
        setFiltroPrivacidad('');
        setFiltroEstado('');
        setFiltroEmpresa('');
    };

    // Opciones para filtros
    const tiposDocumento = ['Obligatorio', 'Opcional'];
    const opcionesPrivacidad = ['Público', 'Privado'];
    const opcionesEstado = ['activo', 'inactivo'];
    const empresasDisponibles = [...new Set(documentos.flatMap(doc => doc.empresas))];

    return (
        <Grid container direction="column" size={{ xs: 12 }}>

            {/* HEADER */}
            <Grid item size={{ xs: 12 }}>
                <SubHeader titulo="Documentación" />
            </Grid>

            {/* TABS */}
            <Grid
                container
                spacing={2}
                sx={{ px: 2, pb: 2 }}
                alignItems="center"
            >
                <Button
                    variant="text"
                    onClick={() => navigate('/personas')}
                    sx={{
                        fontWeight: isActive('/personas') ? 600 : 400,
                        borderBottom: isActive('/personas')
                            ? '2px solid'
                            : '2px solid transparent',
                        borderRadius: 0
                    }}
                >
                    Personas
                </Button>

                <Button
                    variant="text"
                    onClick={() => navigate('/carpetas')}
                    sx={{
                        fontWeight: isActive('/carpetas') ? 600 : 400,
                        borderBottom: isActive('/carpetas')
                            ? '2px solid'
                            : '2px solid transparent',
                        borderRadius: 0
                    }}
                >
                    Carpetas
                </Button>

                <Button
                    variant="text"
                    onClick={() => navigate('/documentacion')}
                    sx={{
                        fontWeight: isActive('/documentacion') ? 600 : 400,
                        borderBottom: isActive('/documentacion')
                            ? '2px solid'
                            : '2px solid transparent',
                        borderRadius: 0
                    }}
                >
                    Documentos
                </Button>
            </Grid>


            {/* ACCIONES */}
            <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                sx={{ px: 2, pb: 2 }}
                gap={2}
            >
                <Grid container spacing={1} size={{ xs: 12, md: 'auto' }}>
                    <TextField
                        size="small"
                        placeholder="Buscar documentos..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: { xs: '100%', md: 300 } }}
                    />

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Tipo</InputLabel>
                        <Select
                            value={filtroTipo}
                            label="Tipo"
                            onChange={(e) => setFiltroTipo(e.target.value)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {tiposDocumento.map(tipo => (
                                <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Privacidad</InputLabel>
                        <Select
                            value={filtroPrivacidad}
                            label="Privacidad"
                            onChange={(e) => setFiltroPrivacidad(e.target.value)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {opcionesPrivacidad.map(priv => (
                                <MenuItem key={priv} value={priv}>{priv}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Estado</InputLabel>
                        <Select
                            value={filtroEstado}
                            label="Estado"
                            onChange={(e) => setFiltroEstado(e.target.value)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {opcionesEstado.map(estado => (
                                <MenuItem key={estado} value={estado}>{estado}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Empresa</InputLabel>
                        <Select
                            value={filtroEmpresa}
                            label="Empresa"
                            onChange={(e) => setFiltroEmpresa(e.target.value)}
                        >
                            <MenuItem value="">Todas</MenuItem>
                            {empresasDisponibles.map(empresa => (
                                <MenuItem key={empresa} value={empresa}>{empresa}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button
                        size="small"
                        variant="outlined"
                        onClick={limpiarFiltros}
                        disabled={!busqueda && !filtroTipo && !filtroPrivacidad && !filtroEstado && !filtroEmpresa}
                    >
                        Limpiar
                    </Button>
                </Grid>

                <Grid container spacing={1} size={{ xs: 12, md: 'auto' }}>
                    <Button variant="outlined" startIcon={<FileDownloadIcon />}>Exportar</Button>
                    <Button variant="outlined" startIcon={<ViewColumnIcon />}>Columnas</Button>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setOpenCreate(true)}
                    >
                        Crear
                    </Button>
                </Grid>
            </Grid>

            {/* ESTADO DE CARGA */}
            {!loaded && (
                <Grid container justifyContent="center" sx={{ py: 4 }}>
                    <CircularProgress />
                </Grid>
            )}

            {/* ERROR */}
            {loaded && error && (
                <Grid container justifyContent="center" sx={{ py: 2 }}>
                    <Typography color="error">{error}</Typography>
                </Grid>
            )}

            {/* CONTENIDO */}
            {loaded && !error && (
                <>
                    {/* SCROLL MOBILE */}
                    <Grid sx={{ width: '100%', overflowX: 'auto' }}>
                        <Grid
                            container
                            direction="column"
                            sx={{ minWidth: { xs: 900, md: 1200 } }}
                        >

                            {/* HEADER TABLA */}
                            <Grid
                                container
                                alignItems="center"
                                sx={{
                                    px: 2,
                                    py: 1,
                                    bgcolor: 'grey.100',
                                    borderTop: '1px solid',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider'
                                }}
                            >
                                <Grid item size={{ xs: 0.5 }} />
                                <Grid item size={{ xs: 3.2 }}>Nombre</Grid>
                                <Grid item size={{ xs: 1.6 }}>Empresas/Clientes</Grid>
                                <Grid item size={{ xs: 1.3 }}>Tipo</Grid>
                                <Grid item size={{ xs: 1.8 }}>Tipo de firma</Grid>
                                <Grid item size={{ xs: 1.1 }}>Privacidad</Grid>
                                <Grid item size={{ xs: 1.1 }}>Entidades</Grid>
                                <Grid item size={{ xs: 1.4 }}>Añadir por defecto</Grid>
                            </Grid>

                            {/* FILAS */}
                            {documentosPaginados.length === 0 ? (
                                <Grid container justifyContent="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">No se encontraron documentos</Typography>
                                </Grid>
                            ) : (
                                documentosPaginados.map((doc) => (
                                    <Grid
                                        key={doc.id}
                                        container
                                        alignItems="center"
                                        sx={{
                                            px: 2,
                                            py: 1,
                                            borderBottom: '1px solid',
                                            borderColor: 'divider',
                                            '&:hover': { bgcolor: 'action.hover' }
                                        }}
                                    >
                                        <Grid item size={{ xs: 0.5 }}>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleOpenMenu(e, doc.id)}
                                            >
                                                <MoreVertIcon fontSize="small" />
                                            </IconButton>
                                            <Menu
                                                anchorEl={menuAnchor}
                                                open={Boolean(menuAnchor) && menuRowId === doc.id}
                                                onClose={handleCloseMenu}
                                                anchorOrigin={{
                                                    vertical: 'bottom',
                                                    horizontal: 'right',
                                                }}
                                                transformOrigin={{
                                                    vertical: 'top',
                                                    horizontal: 'right',
                                                }}
                                            >
                                                <MenuItem onClick={handleOpenEdit}>
                                                    <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
                                                    Ver / Editar
                                                </MenuItem>
                                                <MenuItem
                                                    onClick={() => {
                                                        handleCloseMenu();
                                                        // eliminar(doc.id)
                                                    }}
                                                    sx={{ color: 'error.main' }}
                                                >
                                                    <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                                                    Eliminar
                                                </MenuItem>
                                            </Menu>
                                        </Grid>

                                        <Grid
                                            item
                                            size={{ xs: 3.2 }}
                                            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                        >
                                            <InsertDriveFileIcon color="error" fontSize="small" />
                                            <Typography variant="body2" noWrap>
                                                {doc.nombre}
                                            </Typography>
                                        </Grid>

                                        <Grid item size={{ xs: 1.6 }}>
                                            {doc.empresas.map((empresa, index) => (
                                                <Chip key={index} size="small" label={empresa} sx={{ mr: 0.5, mb: 0.5 }} />
                                            ))}
                                        </Grid>

                                        <Grid item size={{ xs: 1.3 }}>
                                            <Typography variant="body2">{doc.tipo}</Typography>
                                        </Grid>

                                        <Grid item size={{ xs: 1.8 }}>
                                            <Typography variant="body2" noWrap>
                                                {doc.tipo_firma}
                                            </Typography>
                                        </Grid>

                                        <Grid item size={{ xs: 1.1 }}>
                                            <Typography variant="body2">{doc.privacidad}</Typography>
                                        </Grid>

                                        <Grid item size={{ xs: 1.1 }}>
                                            {doc.entidades.map((entidad, index) => (
                                                <Chip key={index} size="small" label={entidad} sx={{ mr: 0.5, mb: 0.5 }} />
                                            ))}
                                        </Grid>

                                        <Grid item size={{ xs: 1.4 }}>
                                            <Chip
                                                size="small"
                                                color={doc.por_defecto ? "success" : "default"}
                                                label={doc.por_defecto ? "Sí" : "No"}
                                            />
                                        </Grid>
                                    </Grid>
                                ))
                            )}
                        </Grid>
                    </Grid>

                    {/* PAGINACIÓN */}
                    {documentosFiltrados.length > 0 && (
                        <Grid
                            container
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ px: 2, py: 2 }}
                            gap={2}
                        >
                            <FormControl variant="standard" sx={{ minWidth: 120 }}>
                                <InputLabel>Filas por página</InputLabel>
                                <Select
                                    value={itemsPerPage}
                                    onChange={(e) => {
                                        setItemsPerPage(e.target.value);
                                        setPage(1);
                                    }}
                                >
                                    <MenuItem value={10}>10</MenuItem>
                                    <MenuItem value={25}>25</MenuItem>
                                    <MenuItem value={50}>50</MenuItem>
                                    <MenuItem value={100}>100</MenuItem>
                                </Select>
                            </FormControl>

                            <Typography variant="body2" color="text.secondary">
                                {documentosFiltrados.length} documentos encontrados
                            </Typography>

                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={handlePageChange}
                                color="primary"
                            />
                        </Grid>
                    )}
                </>
            )}

            {/* MODALES */}
            {editRowId !== null && (
                <EditarDocumento
                    open={Boolean(editRowId)}
                    onClose={() => setEditRowId(null)}
                    documentoId={editRowId}
                />
            )}

            <CrearDocumento
                open={openCreate}
                onClose={() => setOpenCreate(false)}
                onDocumentoCreado={() => {
                    cargarDocumentos();
                    setOpenCreate(false);
                }}
            />
        </Grid>
    );
};
